<template>
	<v-container class="w-100 h-100" >
		<v-card class="mb-4">
			<v-card-text>
				<v-row>
					<v-col><strong>Current Premium:</strong> {{ formatCurrency(premiumSummary.currentPremium) }}</v-col>
					<v-col><strong>New Premium:</strong> {{ formatCurrency(premiumSummary.newPremium) }}</v-col>
					<v-col :class="showDifference(premiumSummary.currentPremium, premiumSummary.newPremium)"><strong>Difference:</strong> {{ getDifference(premiumSummary.currentPremium, premiumSummary.newPremium) }}</v-col>
				</v-row>
			</v-card-text>
		</v-card>

		<v-expansion-panels>
			<v-expansion-panel v-for="(line, index) in premiumSummary.lines" :key="index">
				<v-expansion-panel-title>
					<v-row>
						<v-col>{{ line.insuranceLineCode }} - Line Premiums</v-col>
						<v-col><strong>Current:</strong> {{ formatCurrency(line.currentPremium) }}</v-col>
						<v-col><strong>New:</strong> {{ formatCurrency(line.newPremium) }}</v-col>
						<v-col :class="showDifference(line.currentPremium, line.newPremium)"><strong>Difference:</strong> {{ getDifference(line.currentPremium, line.newPremium) }}</v-col>
					</v-row>
				</v-expansion-panel-title>
				<v-expansion-panel-text>
					<v-divider class="my-2" />
					<h4>Line Coverages</h4>
					<v-row v-if="line.coverages.length > 0">
						<v-col><strong>Code</strong></v-col>
						<v-col class="text-center"><strong>Current</strong></v-col>
						<v-col class="text-center"><strong>New</strong></v-col>
						<v-col class="text-center"><strong>Difference</strong></v-col>
					</v-row>
					<v-list>
						<v-list-item v-for="coverage in line.coverages" :key="coverage.coverageCode">
							<v-row>
								<v-col>{{ coverage.coverageCode }}</v-col>
								<v-col class="text-right">{{ formatCurrency(coverage.currentPremium) }}</v-col>
								<v-col class="text-right">{{ formatCurrency(coverage.newPremium) }}</v-col>
								<v-col :class="['text-right', showDifference(coverage.currentPremium, coverage.newPremium)]">{{ getDifference(coverage.currentPremium, coverage.newPremium) }}</v-col>
							</v-row>
						</v-list-item>
					</v-list>

					<v-divider class="my-2" />
					<h4>Units</h4>
					<v-row v-if="line.units.length > 0" >
						<v-col><strong>Unit:</strong></v-col>
						<v-col><strong>State:</strong></v-col>
						<v-col><strong>Location:</strong></v-col>
						<v-col><strong>SubLocation:</strong></v-col>
						<v-col class="text-center"><strong>Current:</strong></v-col>
						<v-col class="text-center"><strong>New:</strong></v-col>
						<v-col class="text-center"><strong>Difference:</strong></v-col>
					</v-row>
					<v-expansion-panels multiple>
						<v-expansion-panel v-for="(unit, uIndex) in line.units" :key="uIndex">
							<v-expansion-panel-title>
								<v-row>
									<v-col>{{ unit.unit }}</v-col>
									<v-col>{{ unit.state }}</v-col>
									<v-col>{{ unit.location }}</v-col>
									<v-col>{{ unit.sublocation }}</v-col>
									<v-col class="text-right">{{ formatCurrency(unit.currentPremium) }}</v-col>
									<v-col class="text-right">{{ formatCurrency(unit.newPremium) }}</v-col>
									<v-col :class="['text-right', showDifference(unit.currentPremium, unit.newPremium)]">{{ getDifference(unit.currentPremium, unit.newPremium) }}</v-col>
								</v-row>
							</v-expansion-panel-title>
							<v-expansion-panel-text>
								<h5>Coverages</h5>
								<v-row v-if="unit.coverages.length > 0">
									<v-col><strong>Code</strong></v-col>
									<v-col class="text-center"><strong>Current</strong></v-col>
									<v-col class="text-center"><strong>New</strong></v-col>
									<v-col class="text-center"><strong>Difference</strong></v-col>
								</v-row>
								<v-list>
									<v-list-item v-for="coverage in unit.coverages" :key="coverage.coverageCode">
										<v-row>
											<v-col>{{ coverage.coverageCode }}</v-col>
											<v-col class="text-right">{{ formatCurrency(coverage.currentPremium) }}</v-col>
											<v-col class="text-right">{{ formatCurrency(coverage.newPremium) }}</v-col>
											<v-col :class="['text-right', showDifference(coverage.currentPremium, coverage.newPremium)]">{{ getDifference(coverage.currentPremium, coverage.newPremium) }}</v-col>
										</v-row>
									</v-list-item>
								</v-list>
							</v-expansion-panel-text>
						</v-expansion-panel>
					</v-expansion-panels>
				</v-expansion-panel-text>
			</v-expansion-panel>
		</v-expansion-panels>
	</v-container>
</template>

<script lang="ts">
import { Tell } from '@/services/Tell';
import { ref, onMounted, inject } from 'vue';
import { PolicyPremiumSummary } from '@/models/RatingModels';

interface ViewPremiumSummaryProps {
	premiumSummary: PolicyPremiumSummary;
}

export default {
	//name doesn't match the file name, but we're doing it like this for uri simplicity on Entra config
	//and for clarity on the vue app
	name: 'ViewPremiumSummary',
	props: {
		//these properties are exposed to consumers of this component
		premiumSummary: {
			type: PolicyPremiumSummary,
			required: true
		}
	},
	computed: {
	},

	setup(props: ViewPremiumSummaryProps) {
		
		const tell = inject('tell') as Tell;

		const formatCurrency = (amount: number) => {
			// Format the number with commas
			const formatter = new Intl.NumberFormat('en-US');
			let amountStr = formatter.format(amount);

			// Calculate the padding size
			const maxDigits = 7; // 999,999 has 6 digits, and a comma
			const paddingSize = maxDigits - amountStr.length;

			// Pad the string with leading spaces
			amountStr = amountStr.padStart(paddingSize + amountStr.length, ' ');

			return `$${amountStr}`;
		}

		const getDifference = (current: number, newAmount: number) => {
			const difference = newAmount - current;
			return formatCurrency(difference);
		}

		const showDifference = (current: number, newAmount: number) => {
			const difference = newAmount - current;
			//TODO: show something different if the difference is < 0 ?
			return difference === 0 ? 'text-success' : 'text-error';
		}

		// onMounted(async () => {
			
		// });

		return {
			formatCurrency,
			getDifference,
			showDifference,
			policySummary: props.premiumSummary

			//this is the data that is local to this component and reactive
			//use the 'reactive' keyword to make objects and arrays reactive
			//use the 'ref' keyword for simple types
		}
	}
}
</script>
<style scoped>

</style>